import {
  Button,
  Form,
  Input,
  Select,
  message,
  TreeSelect,
  Tag,
  Spin,
  Modal,
  Radio,
  Row,
  Col,
  Collapse,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { fetchProduct } from "../../../../../configuration/module/Product/Service/product";
import { fetchDeviceTypeForFilter } from "../../DeviceType/Service/deviceType";
import TextArea from "antd/es/input/TextArea";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchBuilderById,
  postBuilder,
  putBuilder,
} from "../Service/qualityProcedureBuilder";

const { Option } = Select;

const ACTION_OPTIONS = [
  { value: "REJECT", label: "Reject" },
  { value: "REWORK", label: "Rework" },
];

const QualityProcedureBuilderForm = ({ mode }) => {
  const { uuid } = useParams();
  const [form] = Form.useForm();
  const [records, setRecords] = useState([]);
  const [product, setProduct] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editData, setEditData] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [dimensionPoints, setDimensionPoints] = useState([]);
  const [hoverPoint, setHoverPoint] = useState(null);

  // Convert product API to TreeSelect format
  const buildTreeData = (nodes) =>
    nodes.map((node) => ({
      title: node.productName,
      value: node.id,
      key: node.id,
      // selectable: !node.children?.length,
      // disabled: node.children?.length > 0,
      children: node.children ? buildTreeData(node.children) : [],
    }));

  // Convert File to Base64
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (err) => reject(err);
    });

  // Load initial data
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const productRes = await fetchProduct();
        setProduct(buildTreeData(productRes.data));
        const categoryRes = await fetchDeviceTypeForFilter();
        setCategories(categoryRes.data);
      } catch (err) {
        message.error("Network error");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Load edit mode data
  useEffect(() => {
    if (!uuid) return;

    (async () => {
      setIsLoading(true);
      try {
        const res = await fetchBuilderById(uuid);
        const data = res.data;
        setEditData(data);

        form.setFieldsValue({
          product: data.productId,
          procedureName: data.procedureName,
          operationNumber: data.operationNumber,
          asset: data.asset,
          skipConditions: data.skipConditions,
        });

        // Normalize records from server to local UI shape
        const normalized = (data.records || []).map((r) => ({
          ...r,
          key: r.uuid || Date.now(),
          deviceType: r.deviceTypeId,
          value: r.value,
          unit: r.unit,
          tolerance: r.tolerance,
          controlLimit: r.controlLimit,
          lowerAction: r.lowerAction || null,
          upperAction: r.upperAction || null,
          options: r.options || [],
          files: (r.sampleImages || []).map((img, idx) => ({
            uid: `${r.uuid || "srv"}-${idx}`,
            name: img.fileName || `image-${idx}`, // ✅ ADD
            url: `data:image/png;base64,${img.base64}`, // ✅ FIX
            status: "done",
            originFileObj: null,
            dimensions: img.dimensions || [], // ✅ ADD (for drawn line)
          })),
        }));

        setRecords(normalized);
      } catch (err) {
        message.error("Error loading edit data");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [uuid, form]);

  // Add new record
  const addRecord = () => {
    setRecords([
      ...records,
      {
        key: Date.now(),
        description: "",
        inputType: "",
        options: [],
        files: [],
        value: undefined,
        unit: "",
        tolerance: undefined,
        controlLimit: undefined,
        lowerAction: null,
        upperAction: null,
      },
    ]);
  };

  // Delete record
  const deleteRecord = (key) => {
    setRecords(records.filter((r) => r.key !== key));
  };

  // File selection
  const handleFileSelect = (e, record) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      if (!file.type.startsWith("image/")) {
        message.error("Only images allowed");
        return;
      }

      record.files.push({
        uid: Date.now().toString(),
        name: file.name,
        url: URL.createObjectURL(file),
        originFileObj: file,
        status: "done",
      });
    });

    setRecords([...records]);
    e.target.value = "";
  };

  // Submit logic
  const onFinish = async (values) => {
    console.log("values", values);
    if (records.length === 0) {
      return message.error("Add at least one record.");
    }

    // Validate input fields
    for (let i = 0; i < records.length; i++) {
      const r = records[i];
      if (!r.description || !r.inputType)
        return message.error(`Record ${i + 1} incomplete.`);

      if (r.inputType === "OPTIONS" && (!r.options || r.options.length < 2))
        return message.error(`Record ${i + 1} needs 2+ options.`);

      if (r.inputType === "DOUBLE") {
        // ensure numeric fields present
        if (r.value === undefined || r.value === null || r.value === "") {
          return message.error(`Record ${i + 1}: value is required`);
        }
        if (
          r.tolerance === undefined ||
          r.tolerance === null ||
          r.tolerance === ""
        ) {
          return message.error(`Record ${i + 1}: tolerance is required`);
        }
        if (
          r.controlLimit === undefined ||
          r.controlLimit === null ||
          r.controlLimit === ""
        ) {
          return message.error(`Record ${i + 1}: control limit is required`);
        }
        // ensure actions provided
        if (!r.lowerAction) {
          return message.error(
            `Record ${i + 1}: select action for below threshold`
          );
        }
        if (!r.upperAction) {
          return message.error(
            `Record ${i + 1}: select action for above threshold`
          );
        }
      }
    }

    // Convert files to Base64
    const recordsWithBase64 = await Promise.all(
      records.map(async (r, index) => ({
        id: r.uuid,
        deviceTypeId: r.deviceType,
        description: r.description,
        inputType: r.inputType,
        options: r.options || [],
        value: r.value,
        tolerance: r.tolerance,
        controlLimit: r.controlLimit,
        unit: r.unit,
        lowerAction: r.lowerAction,
        upperAction: r.upperAction,
        sequenceNo: index + 1,
        // sampleImages: await Promise.all(
        //   (r.files || []).map(async (f) =>
        //     f.originFileObj
        //       ? fileToBase64(f.originFileObj)
        //       : f.url.split(",")[1]
        //   )
        // ),
        sampleImages: await Promise.all(
          (r.files || []).map(async (f) => ({
            base64: f.originFileObj
              ? await fileToBase64(f.originFileObj)
              : f.url.split(",")[1],
            fileName: f.name,
            dimensions: f.dimensions || [], // <-- Add this line
          }))
        ),
      }))
    );

    const payload = {
      procedureName: values.procedureName,
      productId: values.product,
      listOfProcedureBuilderRecord: recordsWithBase64,
      asset: values.asset,
      operationNumber: values.operationNumber,
      skipContions: values.skipContions,
    };

    saveData(payload);
  };

  const saveData = async (data) => {
    console.log("data", data);

    setIsLoading(true);
    try {
      if (editData) {
        await putBuilder(data, editData.uuid);
      } else {
        await postBuilder(data);
      }

      messageApi.open({
        type: "success",
        content: `Procedure template ${
          editData ? "updated" : "added"
        } successfully`,
      });

      navigate({
        pathname: "/quality/configuration/procedure-builder",
        replace: true,
      });

      form.resetFields();
    } catch (err) {
      messageApi.open({
        type: "error",
        content: "Error while saving execution",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setDimensionPoints((prev) => {
      // If no line exists or starting new line
      if (prev.length === 0 || prev.length === 2) {
        return [{ x, y }]; // start new line
      }

      // Second click → save line
      const newLine = [prev[0], { x, y }];

      setRecords((old) =>
        old.map((r) => ({
          ...r,
          files: r.files.map((f) =>
            f.url === selectedImage
              ? {
                  ...f,
                  dimensions: [
                    {
                      x1: newLine[0].x,
                      y1: newLine[0].y,
                      x2: newLine[1].x,
                      y2: newLine[1].y,
                    },
                  ],
                }
              : f
          ),
        }))
      );

      setHoverPoint(null);
      return newLine;
    });
  };

  const handleImageMouseMove = (e) => {
    if (dimensionPoints.length !== 1) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setHoverPoint({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };
  useEffect(() => {
    if (!isModalOpen || !selectedImage) return;

    const imageFile = records
      .flatMap((r) => r.files || [])
      .find((f) => f.url === selectedImage);

    if (imageFile?.dimensions?.length) {
      const d = imageFile.dimensions[0];
      setDimensionPoints([
        { x: d.x1, y: d.y1 },
        { x: d.x2, y: d.y2 },
      ]);
    } else {
      setDimensionPoints([]);
    }

    setHoverPoint(null);
  }, [isModalOpen, selectedImage, records]);

  return (
    <Spin spinning={isLoading}>
      {contextHolder}

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col
            span={24}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
              Quality Plan
            </div>
            {mode === "view" || (
              <Button
                icon={<PlusOutlined />}
                type="dashed"
                onClick={addRecord}
                style={{
                  position: "fixed",
                  top: 24,
                  right: 24,
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              >
                Add Record
              </Button>
            )}
          </Col>
          <Col span={12}>
            <Form.Item
              name="product"
              label="Select Product / Part no"
              rules={[
                {
                  required: true,
                  message: "Please select a product / part no!",
                },
              ]}
            >
              <TreeSelect
                treeData={product}
                placeholder="Select Product / Part no"
                allowClear
                style={{ width: "100%" }}
                treeDefaultExpandAll={false}
                disabled={mode === "view"}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Quality Plan Name"
              name="procedureName"
              rules={[
                { required: true, message: "Please enter quality plan name!" },
              ]}
            >
              <Input
                disabled={mode === "view"}
                placeholder="Enter quality plan name"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Operation number"
              name="operationNumber"
              rules={[
                { required: true, message: "Please enter operation number!" },
              ]}
            >
              <Input
                disabled={mode === "view"}
                placeholder="Enter operation number"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Machine / Station"
              name="asset"
              rules={[{ required: true, message: "Please enter asset!" }]}
            >
              <Input disabled={mode === "view"} placeholder="Enter asset" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="If part no / product is rejected at any of the character, should I skip remaining characters ?"
              name="skipConditions"
              rules={[
                {
                  required: true,
                  message: "Please select any of the condition!",
                },
              ]}
            >
              <Radio.Group disabled={mode === "view"}>
                <Radio value={true}>Yes, Skip</Radio>
                <Radio value={false}>No, Don't skip</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {/* ------------------------- ACCORDION ------------------------- */}
        <Collapse accordion>
          {records.map((record, idx) => {
            // compute thresholds for label display (if numeric values exist)
            const numValue =
              record.value !== undefined && record.value !== ""
                ? Number(record.value)
                : null;
            const numTolerance =
              record.tolerance !== undefined && record.tolerance !== ""
                ? Number(record.tolerance)
                : null;
            const lowerThreshold =
              numValue !== null && numTolerance !== null
                ? numValue - numTolerance
                : null;
            const upperThreshold =
              numValue !== null && numTolerance !== null
                ? numValue + numTolerance
                : null;

            return (
              <Collapse.Panel
                key={record.key}
                header={`Character ${idx + 1} — ${
                  // record.description?.slice(0, 20) || "Untitled"
                  record.description || "Untitled"
                }`}
                extra={
                  mode !== "view" && (
                    <DeleteOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecord(record.key);
                      }}
                      style={{ color: "red", marginLeft: 10 }}
                    />
                  )
                }
              >
                {/* <Card bordered style={{ marginBottom: 10 }}> */}
                <Form.Item label="Description" required>
                  <TextArea
                    rows={3}
                    value={record.description}
                    disabled={mode === "view"}
                    onChange={(e) => {
                      record.description = e.target.value;
                      setRecords([...records]);
                    }}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Device Type" required>
                      <Select
                        value={record.deviceType}
                        disabled={mode === "view"}
                        onChange={(v) => {
                          record.deviceType = v;
                          setRecords([...records]);
                        }}
                      >
                        {categories.map((c) => (
                          <Option value={c.id} key={c.id}>
                            {c.deviceType}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item label="Input Type" required>
                      <Select
                        value={record.inputType}
                        disabled={mode === "view"}
                        onChange={(v) => {
                          record.inputType = v;
                          if (v !== "OPTIONS") record.options = [];
                          // reset double-related fields when switching away
                          if (v !== "DOUBLE") {
                            record.value = undefined;
                            record.unit = "";
                            record.tolerance = undefined;
                            record.controlLimit = undefined;
                            record.lowerAction = null;
                            record.upperAction = null;
                          }
                          setRecords([...records]);
                        }}
                      >
                        <Option value="OK_NOT_OK">OK / NOT OK</Option>
                        <Option value="OPTIONS">Options</Option>
                        <Option value="STRING">String</Option>
                        <Option value="DOUBLE">Number</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {/* ----- DOUBLE INPUT FIELDS ----- */}
                {record.inputType === "DOUBLE" && (
                  <>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item
                          label="Value"
                          required
                          rules={[
                            { required: true, message: "Value is required" },
                          ]}
                        >
                          <Input
                            placeholder="Value"
                            disabled={mode === "view"}
                            type="number"
                            value={record.value}
                            onChange={(e) => {
                              record.value = e.target.value;
                              setRecords([...records]);
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={6}>
                        <Form.Item
                          label="Unit"
                          required
                          rules={[
                            { required: true, message: "Unit is required" },
                          ]}
                        >
                          <Input
                            placeholder="Unit"
                            disabled={mode === "view"}
                            value={record.unit}
                            onChange={(e) => {
                              record.unit = e.target.value;
                              setRecords([...records]);
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={6}>
                        <Form.Item
                          label="Tolerance (±)"
                          required
                          rules={[
                            {
                              required: true,
                              message: "Tolerance (±) is required",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Tolerance"
                            disabled={mode === "view"}
                            type="number"
                            value={record.tolerance}
                            onChange={(e) => {
                              record.tolerance = e.target.value;
                              setRecords([...records]);
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={6}>
                        <Form.Item
                          label="Control Limit"
                          required
                          rules={[
                            {
                              required: true,
                              message: "Control Limit is required",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Control Limit"
                            disabled={mode === "view"}
                            type="number"
                            value={record.controlLimit}
                            onChange={(e) => {
                              record.controlLimit = e.target.value;
                              setRecords([...records]);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 10 }}>
                      <Col span={12}>
                        <Form.Item
                          label={`Action below ${
                            Number(record.value) - Number(record.tolerance)
                          }`}
                          required
                          rules={[
                            {
                              required: true,
                              message: "Select action for below threshold",
                            },
                          ]}
                        >
                          <Select
                            value={record.lowerAction}
                            disabled={mode === "view"}
                            placeholder="Select action"
                            onChange={(v) => {
                              record.lowerAction = v;
                              setRecords([...records]);
                            }}
                          >
                            {ACTION_OPTIONS.map((a) => (
                              <Option value={a.value} key={a.value}>
                                {a.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label={`Action above ${
                            Number(record.value) + Number(record.tolerance)
                          }`}
                          required
                          rules={[
                            {
                              required: true,
                              message: "Select action for above threshold",
                            },
                          ]}
                        >
                          <Select
                            value={record.upperAction}
                            disabled={mode === "view"}
                            placeholder="Select action"
                            onChange={(v) => {
                              record.upperAction = v;
                              setRecords([...records]);
                            }}
                          >
                            {ACTION_OPTIONS.map((a) => (
                              <Option value={a.value} key={a.value}>
                                {a.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                {/* ----- OPTIONS ----- */}
                {record.inputType === "OPTIONS" && (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      {record.options.map((opt, i) => (
                        <Tag
                          key={i}
                          closable={mode !== "view"}
                          onClose={() => {
                            record.options = record.options.filter(
                              (_, k) => k !== i
                            );
                            setRecords([...records]);
                          }}
                        >
                          {opt}
                        </Tag>
                      ))}
                    </div>

                    {mode !== "view" && (
                      <Input.Search
                        enterButton="Add"
                        placeholder="New option"
                        onSearch={(val) => {
                          if (!val.trim()) return;
                          // case-insensitive duplicate check
                          const exists = (record.options || []).some(
                            (o) => o.toLowerCase() === val.trim().toLowerCase()
                          );
                          if (exists) {
                            message.warning("Duplicate option not allowed");
                            return;
                          }
                          record.options.push(val.trim());
                          setRecords([...records]);
                        }}
                      />
                    )}
                  </>
                )}

                {/* ----- IMAGES ----- */}
                <div style={{ marginTop: 20 }}>
                  <strong>Sample Images</strong>

                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      flexWrap: "wrap",
                      marginTop: 10,
                    }}
                  >
                    {record.files.map((file, i) => (
                      <div
                        key={file.uid}
                        style={{ position: "relative", maxWidth: "100px" }}
                      >
                        <img
                          src={file.url}
                          alt={file.name || ""}
                          width={90}
                          height={90}
                          style={{
                            objectFit: "cover",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedImage(file.url);
                            // setDimensionPoints([]); // reset dimension
                            setModalOpen(true);
                          }}
                        />

                        {mode !== "view" && (
                          <Button
                            danger
                            size="small"
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              opacity: 0.8,
                            }}
                            onClick={() => {
                              record.files.splice(i, 1);
                              setRecords([...records]);
                            }}
                          >
                            X
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {mode !== "view" && (
                    <>
                      <Button
                        style={{ marginTop: 10 }}
                        type="dashed"
                        onClick={() =>
                          document
                            .getElementById(`file-input-${record.key}`)
                            .click()
                        }
                      >
                        Add Image
                      </Button>

                      <input
                        id={`file-input-${record.key}`}
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileSelect(e, record)}
                      />
                    </>
                  )}
                </div>
                {/* </Card> */}
              </Collapse.Panel>
            );
          })}
        </Collapse>

        <Form.Item style={{ marginTop: 20, textAlign: "right" }}>
          <Button
            style={{ marginRight: 16 }}
            onClick={() =>
              navigate("/quality/configuration/procedure-builder", {
                replace: true,
              })
            }
          >
            Cancel
          </Button>

          {mode !== "view" && (
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          )}
        </Form.Item>
      </Form>

      {/* IMAGE MODAL */}
      <Modal
        open={isModalOpen}
        footer={null}
        onCancel={() => {
          setModalOpen(false);
          // setDimensionPoints([]);
          setHoverPoint(null);
        }}
      >
        <div
          style={{ position: "relative", cursor: "crosshair" }}
          onClick={handleImageClick}
          onMouseMove={handleImageMouseMove}
        >
          <img src={selectedImage} alt="preview" style={{ width: "100%" }} />

          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            <defs>
              {/* Blinking gradient */}
              <linearGradient id="blinkGradient" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="red">
                  <animate
                    attributeName="stop-color"
                    values="white;blue;white"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stopColor="red">
                  <animate
                    attributeName="stop-color"
                    values="white;blue;white"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>

              {/* Inward arrow marker */}
              {/* Dimension arrow – START (points inward) */}
              <marker
                id="dimArrowStart"
                markerWidth="24"
                markerHeight="12"
                refX="14"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                {/* arrow head */}
                <path d="M24 0 L12 6 L24 12 Z" fill="red" />
              </marker>

              {/* Dimension arrow – END (points inward) */}
              <marker
                id="dimArrowEnd"
                markerWidth="24"
                markerHeight="12"
                refX="10"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                {/* arrow head */}
                <path d="M0 0 L12 6 L0 12 Z" fill="red" />
              </marker>
            </defs>

            {/* FINAL line */}
            {dimensionPoints.length === 2 && (
              <line
                x1={dimensionPoints[0].x}
                y1={dimensionPoints[0].y}
                x2={dimensionPoints[1].x}
                y2={dimensionPoints[1].y}
                stroke="url(#blinkGradient)"
                strokeWidth={2}
                markerStart="url(#dimArrowEnd)"
                markerEnd="url(#dimArrowStart)"
              />
            )}

            {/* PREVIEW rubber-band line */}
            {dimensionPoints.length === 1 && hoverPoint && (
              <line
                x1={dimensionPoints[0].x}
                y1={dimensionPoints[0].y}
                x2={hoverPoint.x}
                y2={hoverPoint.y}
                stroke="url(#blinkGradient)"
                strokeWidth={2}
                markerStart="url(#dimArrowEnd)"
                markerEnd="url(#dimArrowStart)"
                strokeDasharray="5,5"
              />
            )}
          </svg>
        </div>
      </Modal>
    </Spin>
  );
};

export default QualityProcedureBuilderForm;
